import React, { memo, useCallback, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { assignProcessColor, formatRelativeTime } from '../../AiActivity.utils';
import {
  LAST_RUN_NEVER_TEXT,
  RUNNING_LABEL,
  RUNNING_OPEN_LINK_LABEL,
  START_BUTTON_LABEL,
  STARTING_BUTTON_LABEL,
} from '../StartAiProcessDrawer.constants';
import { StartAiProcessDrawerTestIds } from '../StartAiProcessDrawer.testIds';

import {
  colorDotStyles,
  descriptionStyles,
  getCardStyles,
  headerLeftStyles,
  headerRightStyles,
  headerRowStyles,
  nameStyles,
  runningRowStyles,
  sublineStyles,
} from './ProcessPickerCard.styles';

const ProcessPickerCard = ({ process, lastRun, liveRun, isStarting, onStart, onOpenLiveRun }) => {
  const liveRunId = liveRun?.id ?? null;

  const onStartClick = useCallback(() => {
    onStart(process);
  }, [onStart, process]);

  const onOpenLiveClick = useCallback(() => {
    if (liveRunId) {
      onOpenLiveRun(liveRunId);
    }
  }, [onOpenLiveRun, liveRunId]);

  const subline = useMemo(() => {
    if (!_.isNil(lastRun?.startedAt)) {
      return `Last run: ${formatRelativeTime(lastRun.startedAt)}`;
    }

    return LAST_RUN_NEVER_TEXT;
  }, [lastRun]);

  const dotColor = useMemo(() => {
    const palette = assignProcessColor(process.id);

    return palette.color;
  }, [process.id]);

  const isRunning = !_.isNil(liveRun);

  return (
    <Box
      data-process-id={process.id}
      data-testid={StartAiProcessDrawerTestIds.PROCESS_CARD}
      sx={getCardStyles({ isRunning })}
    >
      <Box sx={headerRowStyles}>
        <Box sx={headerLeftStyles}>
          <Box sx={colorDotStyles({ backgroundColor: dotColor })} />
          <Typography sx={nameStyles} variant="medium16">
            {process.name}
          </Typography>
        </Box>
        <Box sx={headerRightStyles}>
          <Button
            data-testid={StartAiProcessDrawerTestIds.START_BUTTON}
            disabled={isStarting}
            size="small"
            variant="contained"
            onClick={onStartClick}
          >
            {isStarting ? STARTING_BUTTON_LABEL : START_BUTTON_LABEL}
          </Button>
        </Box>
      </Box>
      {!_.isEmpty(process.description) && (
        <Typography sx={descriptionStyles} variant="regular14">
          {process.description}
        </Typography>
      )}
      <Typography sx={sublineStyles} variant="regular12">
        {subline}
      </Typography>
      {isRunning && (
        <Box sx={runningRowStyles}>
          <Typography variant="medium12">{RUNNING_LABEL}</Typography>
          <Button
            data-testid={StartAiProcessDrawerTestIds.RUNNING_OPEN_LINK}
            size="small"
            variant="text"
            onClick={onOpenLiveClick}
          >
            {RUNNING_OPEN_LINK_LABEL}
          </Button>
        </Box>
      )}
    </Box>
  );
};

ProcessPickerCard.propTypes = {
  process: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
  onOpenLiveRun: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  isStarting: PropTypes.bool,
  lastRun: PropTypes.shape({
    id: PropTypes.string,
    startedAt: PropTypes.string,
  }),
  liveRun: PropTypes.shape({
    id: PropTypes.string,
    startedAt: PropTypes.string,
  }),
};

export default memo(ProcessPickerCard);
